import dayjs, { Dayjs } from "dayjs";

import {
	CreateInvoiceRequest,
	Invoice,
	LocalPaymentMethod,
	UpdateInvoiceRequest,
} from "../../../types/finance/FinanceTypes";
import { PriceEntryMode, TaxCode } from "../../../utils/finance/gst";
import {
	InvoiceLineDraft,
	draftFromItem,
	draftsToRequestItems,
	isBlankDraft,
	newLineDraft,
} from "./invoiceLineDraft";
import type { UserUuid } from "../../../types/identity";
import { isUserUuid } from "../../../utils/auth/userUuid";

/**
 * The pure half of `CreateInvoiceModal` — the form's value shape, the initial values for each of
 * its two modes, and the two request-body builders. Extracted from the `.tsx` so the due-date
 * rule below can be pinned by a test that renders nothing.
 */
export interface InvoiceFormValues {
	userId?: string;
	collectViaStripe?: boolean;
	stripeCustomerId?: string;
	/** How a non-Stripe invoice is collected (PAYLINK/PAYID) — a SET of bank/EFT, link and PayID. */
	localPaymentMethods?: LocalPaymentMethod[];
	priceEntryMode?: PriceEntryMode;
	paymentTermsDays?: number;
	issueDate?: Dayjs;
	dueDate?: Dayjs;
	notes?: string;
	lines?: InvoiceLineDraft[];
}

const asDate = (value?: Dayjs | null): string | undefined =>
	value ? value.format("YYYY-MM-DD") : undefined;

/**
 * The practitioner whose rates price this invoice's appointment-type lines (decision D7), coerced
 * to what the DTO will accept: the practitioner's user uuid; anything else is a 400 on the whole invoice.
 *
 * ⚠️ **Anything unusable becomes `undefined`, which is then dropped from the body entirely.**
 * `forbidNonWhitelisted` makes a bad value a rejected invoice, and omitting the field costs only
 * the override — the server falls back to `default_price`, exactly the behaviour that came before
 * the field existed. A practitioner who cannot be identified must not block a bill.
 *
 * ⚠️ It has to be the SAME value the picker queried the catalogue with, or the snapshot will not
 * match what was quoted. Resolve it ONCE, at the modal, and hand it to both.
 */
export function resolvePractitionerId(value: unknown): UserUuid | undefined {
	return isUserUuid(value) ? value : undefined;
}

/** Drop the trailing row nobody filled in, then map to the wire shape. */
function itemsOf(values: InvoiceFormValues) {
	return draftsToRequestItems((values.lines ?? []).filter((line) => !isBlankDraft(line)));
}

/** Initial values for a NEW invoice: terms drive the due date, which the server computes. */
export function createInitialValues(options: {
	fixedUserId?: string;
	defaultMode: PriceEntryMode;
	defaultTaxCode: TaxCode;
	defaultPaymentTermsDays?: number;
}): InvoiceFormValues {
	return {
		userId: options.fixedUserId,
		collectViaStripe: false,
		// PAYLINK/PAYID: default a new invoice to bank transfer only. Named in BOTH initial-value sets,
		// like every other field, so switching between Create and Edit never leaves a stale value behind.
		localPaymentMethods: [LocalPaymentMethod.BANK_TRANSFER],
		priceEntryMode: options.defaultMode,
		paymentTermsDays: options.defaultPaymentTermsDays,
		issueDate: dayjs(),
		// Explicitly undefined, not absent — see `editInitialValues` for why every field this form
		// can ever hold must be named in BOTH initial-value sets.
		dueDate: undefined,
		lines: [newLineDraft(options.defaultTaxCode)],
	};
}

/**
 * Initial values for EDITING a draft.
 *
 * ⚠️ **`dueDate` is prefilled and `paymentTermsDays` is explicitly `undefined`, and both halves
 * of that are load-bearing.**
 *
 * The bug this closes: an edit that changed nothing silently moved the invoice's due date.
 *   1. `paymentTermsDays` is never a property of a saved invoice — the server consumed it at
 *      create time to derive `dueDate` and stored only the date. So there is nothing to prefill
 *      it FROM, and an edit body that carries a term but no date makes the server recompute
 *      `dueDate = issueDate + thatTerm`.
 *   2. Worse, the term it carried was not even this invoice's. antd's `Form` instance and its
 *      store outlive `destroyOnClose` (which unmounts the children, not the store), so a
 *      `paymentTermsDays` typed during an earlier *Create* session was still sitting in the store
 *      when the next *Edit* opened — and `setFieldsValue` MERGES, it does not replace, so an
 *      absent key left the stale one in place.
 *
 * Naming it here with `undefined` is what makes `setFieldsValue` clear it. Sending `dueDate`
 * explicitly is what makes the outcome unambiguous regardless: the DTO ignores
 * `paymentTermsDays` whenever `dueDate` is supplied.
 */
export function editInitialValues(
	invoice: Invoice,
	defaults: { defaultMode: PriceEntryMode; defaultTaxCode: TaxCode },
): InvoiceFormValues {
	return {
		userId: invoice.userId,
		collectViaStripe: !!invoice.collectViaStripe,
		stripeCustomerId: invoice.stripeCustomerId ?? undefined,
		// PAYLINK/PAYID: reflect the invoice's stored set (named here too, per the both-sets rule).
		// `buildUpdateInvoiceBody` never forwards it — the methods are fixed at create, matching the
		// backend `UpdateInvoiceDto`, which has no such field. A pre-PAYLINK row with no set defaults
		// to bank transfer only.
		localPaymentMethods:
			invoice.localPaymentMethods && invoice.localPaymentMethods.length
				? invoice.localPaymentMethods
				: [LocalPaymentMethod.BANK_TRANSFER],
		priceEntryMode: (invoice.priceEntryMode as PriceEntryMode) ?? defaults.defaultMode,
		// ⚠️ Never omit this key. See the docblock — an omitted key leaves the previous session's
		// value in antd's store, and the server then recomputes this invoice's due date from it.
		paymentTermsDays: undefined,
		issueDate: invoice.issueDate ? dayjs(invoice.issueDate) : undefined,
		dueDate: invoice.dueDate ? dayjs(invoice.dueDate) : undefined,
		notes: invoice.notes ?? undefined,
		lines:
			invoice.items && invoice.items.length > 0
				? [...invoice.items]
						.sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
						.map(draftFromItem)
				: [newLineDraft(defaults.defaultTaxCode)],
	};
}

/**
 * `POST /invoices`. `dueDate` is left to the server, derived from `paymentTermsDays`.
 *
 * `practitionerId` is a separate argument rather than a form field because it is not one — nothing
 * on this form edits it. It is resolved once by the caller and handed to BOTH the catalogue picker
 * and this builder, which is what keeps the price the practitioner was quoted and the price the
 * server snapshots the same number.
 */
export function buildCreateInvoiceBody(
	values: InvoiceFormValues,
	practitionerId?: UserUuid,
): CreateInvoiceRequest {
	return {
		userId: String(values.userId),
		practitionerId: resolvePractitionerId(practitionerId),
		collectViaStripe: values.collectViaStripe,
		stripeCustomerId: values.collectViaStripe ? values.stripeCustomerId : undefined,
		// PAYLINK/PAYID: only meaningful for a non-Stripe invoice. Omit it for a Stripe-collected one,
		// which is paid through its hosted page regardless — the server would ignore it anyway.
		localPaymentMethods: values.collectViaStripe ? undefined : values.localPaymentMethods,
		priceEntryMode: values.priceEntryMode,
		paymentTermsDays: values.paymentTermsDays,
		issueDate: asDate(values.issueDate),
		notes: values.notes,
		items: itemsOf(values),
	};
}

/**
 * `PUT /invoices/:id` — drafts only.
 *
 * ⚠️ Two rules, both about not changing something the operator did not touch:
 *
 *   - **`dueDate` is sent and `paymentTermsDays` is NOT.** The DTO accepts both and the date
 *     wins; sending the date is what guarantees an edit that changes nothing leaves the due date
 *     alone. `paymentTermsDays` is deliberately never forwarded on this path — there is no
 *     trustworthy value for it (see `editInitialValues`).
 *   - **`items` is ALWAYS sent alongside `priceEntryMode`.** A mode-only edit on a legacy
 *     flat-tax invoice is a 400 ("Re-supply line items to change price entry mode on this
 *     invoice"); sending both makes that case unreachable from this form.
 *
 * `practitionerId` is re-sent here for the same reason it is sent on create, and it matters MORE
 * on this path: this endpoint replaces the line set wholesale, so every snapshot is re-resolved
 * from scratch. An edit that omitted it would re-price every appointment-type line at
 * `default_price`, silently, on an invoice that had been quoted at the custom rate.
 */
export function buildUpdateInvoiceBody(
	values: InvoiceFormValues,
	practitionerId?: UserUuid,
): UpdateInvoiceRequest {
	return {
		items: itemsOf(values),
		practitionerId: resolvePractitionerId(practitionerId),
		priceEntryMode: values.priceEntryMode,
		issueDate: asDate(values.issueDate),
		dueDate: asDate(values.dueDate),
		collectViaStripe: values.collectViaStripe,
		stripeCustomerId: values.collectViaStripe ? values.stripeCustomerId : undefined,
		notes: values.notes,
	};
}
